import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import publicApiClient from '@services/api/publicClient'
import { conversationService } from '@services/api/messages'
import { storageURL, mediaUrl } from '@config'
import headerImage from '@assets/images/header_accueil.png'
import kudhiPhone from '@assets/images/img_KUDHI.png'
import avatarPlaceholder from '@assets/images/image_01.png'
import { CATEGORIES } from '@constants/categories'
import { useAuthStore } from '@store/authStore'
import { encodeId } from '@utils/idCipher'

function DashboardPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  useEffect(() => {
    document.title = 'KUDHI Market'
    return () => { document.title = 'KUDHI' }
  }, [])
  const [activeCategory, setActiveCategory] = useState(null)
  const [activeTab, setActiveTab] = useState('articles') // Nouvel état pour les onglets
  const [searchQuery, setSearchQuery] = useState('')

  // Fonction pour formater les prix en FCFA
  const formatPrice = (price) => {
    const priceNum = typeof price === 'string' ? parseFloat(price.replace(/[^0-9]/g, '')) : price
    if (!priceNum || priceNum === 0) return ''
    return new Intl.NumberFormat('fr-FR').format(priceNum) + ' FCFA'
  }
  const [videoCarouselIndex, setVideoCarouselIndex] = useState(0)
  const [bannerCarouselIndex, setBannerCarouselIndex] = useState(0)
  const [discountCarouselIndex, setDiscountCarouselIndex] = useState(0)
  const [isFlashResetting, setIsFlashResetting] = useState(false)
  const [showNewPostModal, setShowNewPostModal] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [currentPage, setCurrentPage] = useState(3)
  const [videos, setVideos] = useState([])
  const [loadingVideos, setLoadingVideos] = useState(true)
  const [banners, setBanners] = useState([])
  const [loadingBanners, setLoadingBanners] = useState(true)
  const [adBanners, setAdBanners] = useState([]) // Bannières publicitaires
  const [loadingAdBanners, setLoadingAdBanners] = useState(true)
  const [adVideos, setAdVideos] = useState([]) // Vidéos publicitaires
  const [loadingAdVideos, setLoadingAdVideos] = useState(true)
  const [adPosts, setAdPosts] = useState([]) // Publications publicitaires
  const [loadingAdPosts, setLoadingAdPosts] = useState(true)
  const [adBannerIndex, setAdBannerIndex] = useState(0)
  const [adVideoIndex, setAdVideoIndex] = useState(0)
  const [adPostIndex, setAdPostIndex] = useState(0)
  const [isAdPostHovered, setIsAdPostHovered] = useState(false) // Pour arrêter le carrousel au hover
  const [recentContacts, setRecentContacts] = useState([])
  const [loadingContacts, setLoadingContacts] = useState(true)

  // Charger les bannières depuis l'API
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoadingBanners(true)
        const response = await publicApiClient.get('/posts/type', { params: { type: 3, exclude_staff: 1 } })
        const data = response.data || []
        setBanners(data.map(b => ({
          banniere_id: b.post_id,
          titre: b.libele,
          lien_url: b.siteWeb,
          image_url: b.fichiers?.[0]?.chemin ? mediaUrl(b.fichiers[0].chemin) : null,
        })))
      } catch {
        setBanners([])
      } finally {
        setLoadingBanners(false)
      }
    }
    fetchBanners()
  }, [])

  // Charger les vidéos depuis l'API
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoadingVideos(true)
        const isVideoFile = (path) => /\.(mp4|mov|avi|webm|mkv)$/i.test(path)
        const response = await publicApiClient.get('/posts/type', { params: { type: 1, exclude_staff: 1 } })
        const data = response.data || []
        setVideos(data.map(v => ({
          id: v.post_id,
          titre: v.libele,
          description: v.description,
          views: v.vues || 0,
          likes: v.post_likes_count ?? v.likes ?? 0,
          comments: v.post_comments_count ?? v.comments ?? 0,
          video_url: (() => {
            const f = v.fichiers?.find(f => isVideoFile(f.chemin))
            return f ? mediaUrl(f.chemin) : null
          })(),
          thumbnail_url: (() => {
            const f = v.fichiers?.find(f => !isVideoFile(f.chemin))
            return f ? mediaUrl(f.chemin) : null
          })(),
        })))
      } catch {
        setVideos([])
      } finally {
        setLoadingVideos(false)
      }
    }
    fetchVideos()
  }, [])

  // Charger les bannières publicitaires (publiées par Admin/Super Admin/Staff)
  useEffect(() => {
    const fetchAdBanners = async () => {
      try {
        setLoadingAdBanners(true)
        const response = await publicApiClient.get('/posts/type', { params: { type: 3, staff_only: 1 } })
        const data = response.data || []
        setAdBanners(data.map(b => ({
          banniere_id: b.post_id,
          id: b.post_id,
          titre: b.libele,
          lien_url: b.siteWeb,
          image_url: b.fichiers?.[0]?.chemin ? mediaUrl(b.fichiers[0].chemin) : null,
          views: b.vues || 0,
          likes: b.post_likes_count ?? b.likes ?? 0,
          comments: b.post_comments_count ?? 0,
        })))
      } catch {
        setAdBanners([])
      } finally {
        setLoadingAdBanners(false)
      }
    }
    fetchAdBanners()
  }, [])

  // Charger les vidéos publicitaires (publiées par Admin/Super Admin/Staff)
  useEffect(() => {
    const fetchAdVideos = async () => {
      try {
        setLoadingAdVideos(true)
        const isVideoFile = (path) => /\.(mp4|mov|avi|webm|mkv)$/i.test(path)
        const response = await publicApiClient.get('/posts/type', { params: { type: 1, staff_only: 1 } })
        const data = response.data || []
        setAdVideos(data.map(v => ({
          id: v.post_id,
          titre: v.libele,
          description: v.description,
          views: v.vues || 0,
          likes: v.post_likes_count ?? v.likes ?? 0,
          comments: v.post_comments_count ?? 0,
          video_url: (() => {
            const f = v.fichiers?.find(f => isVideoFile(f.chemin))
            return f ? mediaUrl(f.chemin) : null
          })(),
          thumbnail_url: (() => {
            const f = v.fichiers?.find(f => !isVideoFile(f.chemin))
            return f ? mediaUrl(f.chemin) : null
          })(),
        })))
      } catch {
        setAdVideos([])
      } finally {
        setLoadingAdVideos(false)
      }
    }
    fetchAdVideos()
  }, [])

  // Charger les publications publicitaires (publiées par Admin/Super Admin/Staff)
  useEffect(() => {
    const fetchAdPosts = async () => {
      try {
        setLoadingAdPosts(true)
        const response = await publicApiClient.get('/posts/type', { params: { type: 2, staff_only: 1, limit: 100 } })
        const data = response.data || []
        setAdPosts(data.map(p => {
          const prixNum = parseFloat(String(p.prix || 0).replace(/[^0-9.]/g, '')) || 0
          const newPriceNum = parseFloat(String(p.new_price || 0).replace(/[^0-9.]/g, '')) || 0
          const marge = parseInt(String(p.marge_reduite ?? '').replace(/[^0-9]/g, '')) || 0
          const { dealPrice, oldPrice, reductionPct } = resolvePricing(prixNum, newPriceNum, marge, p)
          const oldPriceNum = oldPrice || 0
          const conditionRaw = p.etat_appareil || p.condition || ''
          const conditionLabels = {
            // valeurs lowercase (articles standards)
            neuf: 'Neuf', scelle: 'Scellé', reconditionne: 'Reconditionné',
            quasi_neuf: 'Quasi neuf', excellent: 'Excellent',
            bon: 'Bon état', moyen: 'État moyen', usage: 'Usagé',
            // valeurs supplémentaires (admin)
            occasion: 'Occasion', 'reconditionné': 'Reconditionné',
          }
          const conditionKey = conditionRaw.toLowerCase().replace(/é/g, 'e').replace(/\s/g, '_')
          const etat_label = conditionLabels[conditionRaw.toLowerCase()] || conditionLabels[conditionKey] || (conditionRaw || '')
          return {
            id: p.post_id,
            libele: p.libele,
            price: dealPrice,
            oldPrice: oldPriceNum > 0 ? oldPriceNum : null,
            discount: reductionPct,
            etat_appareil: conditionRaw.toLowerCase(),
            etat_label: etat_label,
            vues: p.vues || 0,
            likes_count: p.post_likes_count ?? p.likes ?? 0,
            comments_count: p.post_comments_count ?? 0,
            images: p.fichiers?.map(f => mediaUrl(f.chemin)) || [],
          }
        }))
      } catch {
        setAdPosts([])
      } finally {
        setLoadingAdPosts(false)
      }
    }
    fetchAdPosts()
  }, [])

  // Charger les contacts récents (issus des conversations de l'utilisateur)
  useEffect(() => {
    if (!user?.client_id) {
      setRecentContacts([])
      setLoadingContacts(false)
      return
    }

    const fetchContacts = async () => {
      try {
        setLoadingContacts(true)
        const res = await conversationService.getConversations()
        if (res.success) {
          const contacts = (res.data || [])
            .filter(c => c.other_client)
            .slice(0, 8)
            .map(c => ({
              id:         c.other_client.client_id,
              first_name: c.other_client.prenom || '',
              name:       c.other_client.nom || '',
              full_name:  [c.other_client.prenom, c.other_client.nom].filter(Boolean).join(' ') || 'Utilisateur',
              phone:      c.other_client.numero || '',
              avatar:     c.other_client.photo_profil || null,
            }))
          setRecentContacts(contacts)
        }
      } catch {
        setRecentContacts([])
      } finally {
        setLoadingContacts(false)
      }
    }

    fetchContacts()
  }, [user])

  // Carrousel automatique pour la bannière
  useEffect(() => {
    if (banners.length > 0) {
      const interval = setInterval(() => {
        setBannerCarouselIndex((prevIndex) => (prevIndex + 1) % banners.length)
      }, 5000) // Change toutes les 5 secondes
      return () => clearInterval(interval)
    }
  }, [banners])

  // Carrousel automatique pour les vidéos
  useEffect(() => {
    if (videos.length > 0) {
      const interval = setInterval(() => {
        setVideoCarouselIndex((prevIndex) => (prevIndex + 1) % videos.length)
      }, 8000) // Change toutes les 8 secondes
      return () => clearInterval(interval)
    }
  }, [videos])

  // Carrousel automatique pour les bannières publicitaires
  useEffect(() => {
    if (adBanners.length > 0) {
      const interval = setInterval(() => {
        setAdBannerIndex((prevIndex) => (prevIndex + 1) % adBanners.length)
      }, 15000) // Change toutes les 15 secondes
      return () => clearInterval(interval)
    }
  }, [adBanners])

  // Carrousel automatique pour les vidéos publicitaires
  useEffect(() => {
    if (adVideos.length > 0) {
      const interval = setInterval(() => {
        setAdVideoIndex((prevIndex) => (prevIndex + 1) % adVideos.length)
      }, 15000) // Change toutes les 15 secondes
      return () => clearInterval(interval)
    }
  }, [adVideos])

  // Carrousel automatique pour les publications publicitaires (s'arrête au hover)
  useEffect(() => {
    if (adPosts.length > 0 && !isAdPostHovered) {
      const interval = setInterval(() => {
        setAdPostIndex((prevIndex) => (prevIndex + 1) % adPosts.length)
      }, 6000) // Change toutes les 6 secondes
      return () => clearInterval(interval)
    }
  }, [adPosts, isAdPostHovered])

  // Images bannières par défaut si aucune bannière n'est chargée
  const defaultBannerImage = headerImage

  // Composants d'icônes SVG
  const CategoryIcon = ({ id }) => {
    const icons = {
      1: ( // Toutes les catégories
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M4 6h16v2H4zm0 5h16v2H4zm0 5h16v2H4z"/>
        </svg>
      ),
      2: ( // Auto - Moto
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
        </svg>
      ),
      3: ( // Immobilier
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
        </svg>
      ),
      4: ( // Informatique
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
        </svg>
      ),
      5: ( // Vêtements
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 4l1.29 2.57L20 7.86 16.29 10l-.86 2.86L12 11 8.57 12.86 7.71 10 4 7.86l2.71-1.29L8 4h8m0-2H8L6 5 2 7l5 3-1 4 6-2 6 2-1-4 5-3-4-2-2-3z"/>
        </svg>
      ),
      6: ( // Loisirs
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      ),
      7: ( // Services
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
        </svg>
      ),
      8: ( // Décorations
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 22C6.49 22 2 17.51 2 12S6.49 2 12 2s10 4.04 10 9c0 3.31-2.69 6-6 6h-1.77c-.28 0-.5.22-.5.5 0 .12.05.23.13.33.41.47.64 1.06.64 1.67 0 1.38-1.12 2.5-2.5 2.5zm0-18c-4.41 0-8 3.59-8 8s3.59 8 8 8c.28 0 .5-.22.5-.5 0-.16-.08-.28-.14-.35-.41-.46-.63-1.05-.63-1.65 0-1.38 1.12-2.5 2.5-2.5H16c2.21 0 4-1.79 4-4 0-3.86-3.59-7-8-7z"/><circle cx="6.5" cy="11.5" r="1.5"/><circle cx="9.5" cy="7.5" r="1.5"/><circle cx="14.5" cy="7.5" r="1.5"/><circle cx="17.5" cy="11.5" r="1.5"/>
        </svg>
      ),
      9: ( // Modes
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z"/>
        </svg>
      ),
      10: ( // Bijoux
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5L2 9l10 12L22 9l-3-6zM9.62 8l1.5-3h1.76l1.5 3H9.62zM11 10v6.68L5.44 10H11zm2 0h5.56L13 16.68V10z"/>
        </svg>
      ),
      11: ( // Sacs
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-8 4c0 .55-.45 1-1 1s-1-.45-1-1V8h2v2zm2-6c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm4 6c0 .55-.45 1-1 1s-1-.45-1-1V8h2v2z"/>
        </svg>
      ),
      12: ( // Chaussures
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.65 3.5C10.33 2.91 7 3.5 7 3.5V4c0 1.66-1.34 3-3 3v1c0 2.76 2.24 5 5 5 1.38 0 2.63-.56 3.54-1.46L21 20v-2l-5-5V7.5c0-2.21-1.79-4-4-4l-.35.05z"/>
        </svg>
      ),
      13: ( // Chemises (ajout comme alternative)
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 4l1.29 2.57L20 7.86 16.29 10l-.86 2.86L12 11 8.57 12.86 7.71 10 4 7.86l2.71-1.29L8 4h8z"/>
        </svg>
      ),
      14: ( // Autres
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/>
        </svg>
      ),
    }
    return icons[id] || icons[14]
  }

  // Catégories complètes pour LIGDI

  // Catégories dynamiques depuis l'API
  const [categoriesFromDB, setCategoriesFromDB] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)

  // Charger les catégories depuis l'API (filtrées selon CATEGORIES)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true)
        const response = await publicApiClient.get('/categories')
        const data = response.data || []
        const filtered = data.filter(c =>
          c.libele_categorie !== 'Toutes les catégories'
        )
        setCategoriesFromDB([
          { categorie_id: null, libele_categorie: 'Toutes les catégories', icon: 'apps' },
          ...filtered,
        ])
      } catch {
        setCategoriesFromDB([])
      } finally {
        setLoadingCategories(false)
      }
    }
    fetchCategories()
  }, [])

  // Produits dynamiques depuis l'API

  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const pageSize = 12
  const prevCategoryRef = useRef(activeCategory)

  const CONDITION_LABELS = {
    neuf:          'Neuf',
    scelle:        'Scellé',
    reconditionne: 'Reconditionné',
    quasi_neuf:    'Quasi neuf',
    excellent:     'Excellent',
    bon:           'Bon état',
    moyen:         'État moyen',
    usage:         'Usagé',
  }

  // Autoplay carrousel Ventes Flash — boucle infinie via clones
  useEffect(() => {
    const discountedProducts = products.filter(p => p.oldPrice)
    const N = discountedProducts.length
    if (N < 4) return
    const timer = setInterval(() => {
      setDiscountCarouselIndex(i => {
        const next = i + 1
        if (next >= N) {
          // On entre dans les clones : reset silencieux après la transition
          setTimeout(() => {
            setIsFlashResetting(true)
            setDiscountCarouselIndex(0)
            setTimeout(() => setIsFlashResetting(false), 50)
          }, 520)
        }
        return next
      })
    }, 3500)
    return () => clearInterval(timer)
  }, [products])

  const parseReduction = (r) => {
    if (r === null || r === undefined || r === '' || r === 'Choisissez un champ') return null
    const num = parseInt(String(r).replace(/[^0-9]/g, ''))
    return isNaN(num) || num <= 0 || num >= 100 ? null : num
  }

  // Détermine le prix affiché (deal), l'ancien prix (barré) et le % de réduction.
  // Gère la convention actuelle (prix=original, new_price=prix réduit, marge_reduite=%)
  // ET l'ancienne (new_price = ancien prix) pour les données déjà en base.
  const resolvePricing = (prixNum, newPriceNum, marge, p) => {
    if (marge > 0 && newPriceNum > 0 && newPriceNum < prixNum) {
      return { dealPrice: newPriceNum, oldPrice: prixNum, reductionPct: marge }
    }
    if (newPriceNum > prixNum && newPriceNum > 0) {
      return { dealPrice: prixNum, oldPrice: newPriceNum, reductionPct: Math.round((1 - prixNum / newPriceNum) * 100) }
    }
    const r = parseReduction(p?.reduction ?? p?.discount)
    if (r > 0 && prixNum > 0) {
      return { dealPrice: prixNum, oldPrice: Math.round(prixNum / (1 - r / 100)), reductionPct: r }
    }
    return { dealPrice: prixNum, oldPrice: null, reductionPct: 0 }
  }

  const mapProduct = (p) => {
    const prixNum     = parseFloat(String(p.prix).replace(/[^0-9.]/g, '')) || 0
    const newPriceNum = p.new_price ? parseFloat(String(p.new_price).replace(/[^0-9.]/g, '')) : 0
    const marge       = parseInt(String(p.marge_reduite ?? '').replace(/[^0-9]/g, '')) || 0
    const conditionKey = p.etat_appareil || ''
    const { dealPrice, oldPrice, reductionPct } = resolvePricing(prixNum, newPriceNum, marge, p)
    return {
      id:                   p.post_id,
      name:                 p.libele,
      price:                dealPrice,
      oldPrice:             oldPrice,
      reduction:            reductionPct,
      etat_appareil:        conditionKey,
      etat_appareil_label:  CONDITION_LABELS[conditionKey] || '',
      image:                (() => { const f = p.fichiers?.find(f => f.is_main) ?? p.fichiers?.[0]; return f?.chemin ? mediaUrl(f.chemin) : null })(),
      views:                p.vues || 0,
      likes:                p.post_likes_count ?? p.likes ?? 0,
      comments:             p.post_comments_count ?? p.comments ?? 0,
      client_id:            p.client?.client_id || p.post_client_id || null,
      client_name:          p.client ? [p.client.nom, p.client.prenom].filter(Boolean).join(' ') : '',
      client_phone:         p.client?.numero || '',
      client_photo:         p.client?.photo_profil ? mediaUrl(p.client.photo_profil) : null,
      category_id:          p.post_categorie_id,
      date:                 p.created_at
                              ? new Date(p.created_at).toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' })
                              : '',
    }
  }

  // Un seul effet — reset page si catégorie change, annule les requêtes obsolètes
  useEffect(() => {
    // Si la catégorie vient de changer et qu'on n'est pas à la page 1 :
    // on reset la page → le changement de `page` va re-déclencher cet effet
    if (prevCategoryRef.current !== activeCategory) {
      prevCategoryRef.current = activeCategory
      if (page !== 1) {
        setPage(1)
        return
      }
    }

    const controller = new AbortController()

    const load = async () => {
      try {
        setLoadingProducts(true)
        const params = { page, limit: pageSize, type: 2, status: 2, exclude_staff: 1 }
        if (activeCategory !== null) params.post_categorie_id = activeCategory
        const response = await publicApiClient.get('/posts', { params, signal: controller.signal })
        const posts = response.data || []
        setProducts(Array.isArray(posts) ? posts.map(mapProduct) : [])
        setTotalPages(Math.ceil((response.total || posts.length || 0) / pageSize))
      } catch (err) {
        // Ignorer les erreurs d'annulation (requête obsolète)
        if (err?.code !== 'ERR_CANCELED' && err?.name !== 'CanceledError') {
          setProducts([])
          setTotalPages(1)
        }
      } finally {
        setLoadingProducts(false)
      }
    }

    load()
    return () => controller.abort()
  }, [page, activeCategory])

  // Fonction pour changer de page avec AJAX
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) {
      setPage(newPage)
      // Scroll vers le haut de la liste des produits
      window.scrollTo({ top: 400, behavior: 'smooth' })
    }
  }

  // Contacts

  const contacts = [
    { name: 'Alain Yahaut', avatar: avatarPlaceholder },
    { name: 'Romaric Konan', avatar: avatarPlaceholder },
    { name: 'Paul Kouamé', avatar: avatarPlaceholder },
    { name: 'Alain Koffi', avatar: avatarPlaceholder },
  ]

  // Vérifier si l'utilisateur est admin, super admin ou staff
  const isAdminOrStaff = user && ['Admin', 'Super Admin', 'Staff'].includes(user.role)

  // Recherche locale sur les produits chargés
  const query = searchQuery.trim().toLowerCase()
  const visibleProducts = query
    ? products.filter(p => (p.name || '').toLowerCase().includes(query) || (p.client_name || '').toLowerCase().includes(query))
    : products
  const activeCategoryLabel = categoriesFromDB.find(c => c.categorie_id === activeCategory)?.libele_categorie || 'Toutes les catégories'
  const currentVideo = videos[videoCarouselIndex]

  const openConversation = (product) => navigate('/messages', {
    state: {
      openConversationWith: {
        id:     product.client_id,
        name:   product.client_name,
        phone:  product.client_phone,
        avatar: product.client_photo,
        article: { name: product.name, url: `/product/${encodeId(product.id)}`, image: product.image },
      },
    },
  })

  const openWhatsApp = (phone) => {
    if (!phone) return
    let digits = phone.replace(/\D/g, '')
    if (digits.startsWith('0') && digits.length <= 10) digits = '225' + digits.slice(1)
    window.open(`https://wa.me/${digits}`, '_blank')
  }

  return (
    <div className="home-font bg-slate-50 min-h-screen">

      {/* ============ VIDÉO + BANNIÈRES ============ */}
      <section className="w-[94%] md:w-[80%] mx-auto pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Vidéo à la une */}
          <div className="group relative lg:col-span-2 h-56 sm:h-72 lg:h-[400px] overflow-hidden rounded-md bg-[#001A33]">
            {loadingVideos ? (
              <div className="absolute inset-0 skeleton-shimmer opacity-20" />
            ) : currentVideo ? (
              <>
                {currentVideo.video_url ? (
                  <video
                    key={currentVideo.id}
                    className="absolute inset-0 h-full w-full object-cover cursor-pointer"
                    poster={currentVideo.thumbnail_url || undefined}
                    autoPlay
                    muted
                    loop
                    playsInline
                    onClick={() => navigate(`/video/${encodeId(currentVideo.id)}`)}
                  >
                    <source src={currentVideo.video_url} type="video/mp4" />
                  </video>
                ) : currentVideo.thumbnail_url ? (
                  <img src={currentVideo.thumbnail_url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                ) : null}

                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#001A33]/85 to-transparent" />

                <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-md bg-[#FFD700] px-2.5 py-1 text-xs font-bold text-[#002A4E]">
                  <Svg d={PATHS.play} className="w-3.5 h-3.5" fill />
                  Vidéo à la une
                </span>

                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-4 sm:p-6">
                  <div className="min-w-0">
                    {currentVideo.titre && (
                      <h2 className="text-lg sm:text-2xl font-extrabold tracking-tight text-white line-clamp-1">{currentVideo.titre}</h2>
                    )}
                    <div className="mt-1.5 flex items-center gap-3 text-xs sm:text-sm text-white/80">
                      <span className="inline-flex items-center gap-1"><Svg d={PATHS.eye} className="w-4 h-4" />{currentVideo.views ?? 0}</span>
                      <span className="inline-flex items-center gap-1"><Svg d={PATHS.heart} className="w-4 h-4" />{currentVideo.likes ?? 0}</span>
                      <span className="inline-flex items-center gap-1"><Svg d={PATHS.chat} className="w-4 h-4" />{currentVideo.comments ?? 0}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/video/${encodeId(currentVideo.id)}`)}
                    className="inline-flex flex-shrink-0 items-center gap-2 rounded-md bg-white px-4 py-2.5 text-sm font-bold text-[#002A4E] transition-colors hover:bg-[#FFD700]"
                  >
                    <Svg d={PATHS.play} className="w-4 h-4" fill />
                    Regarder
                  </button>
                </div>

                {videos.length > 1 && (
                  <div className="absolute right-4 top-4 flex gap-1.5">
                    {videos.map((_, index) => (
                      <button
                        key={index}
                        aria-label={`Vidéo ${index + 1}`}
                        onClick={() => setVideoCarouselIndex(index)}
                        className={`h-1.5 rounded-full transition-all ${index === videoCarouselIndex ? 'w-6 bg-[#FFD700]' : 'w-1.5 bg-white/60 hover:bg-white'}`}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white/50">
                <Svg d={PATHS.play} className="w-12 h-12 mb-2" fill />
                <p className="text-sm">Aucune vidéo disponible</p>
              </div>
            )}
          </div>

          {/* Bannières empilées */}
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-4 lg:h-[400px]">
            {loadingBanners ? (
              <div className="h-28 sm:h-36 lg:h-auto rounded-md skeleton-shimmer" />
            ) : (
              [0].map((offset) => {
                const b = banners.length ? banners[(bannerCarouselIndex + offset) % banners.length] : null
                return (
                  <div
                    key={offset}
                    onClick={() => { if (b?.banniere_id) navigate(`/banner/${encodeId(b.banniere_id)}`) }}
                    className={`group relative h-28 sm:h-36 lg:h-auto overflow-hidden rounded-md bg-white ring-1 ring-slate-200/70 ${b ? 'cursor-pointer' : ''}`}
                  >
                    <img
                      key={`${offset}-${bannerCarouselIndex}`}
                      src={b?.image_url || defaultBannerImage}
                      alt={b?.titre || 'Bannière'}
                      className="h-full w-full object-cover animate-fade-in transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => { e.target.src = defaultBannerImage }}
                    />
                  </div>
                )
              })
            )}

            {/* Espace publicitaire KUDHI */}
            <div className="relative h-28 sm:h-36 lg:h-auto overflow-hidden rounded-md bg-gradient-to-br from-[#FFE45C] via-[#FFD700] to-[#F5B700]">
              <div className="absolute inset-0 home-grid-bg-light pointer-events-none" />
              <img src={kudhiPhone} alt="" className="pointer-events-none absolute -bottom-16 right-2 h-[130%] w-auto max-w-none rotate-[8deg] drop-shadow-[0_15px_25px_rgba(0,42,78,0.35)]" />
              <div className="relative flex h-full max-w-[62%] flex-col justify-center p-3 sm:p-5">
                <span className="hidden sm:inline-flex w-fit items-center gap-1 rounded bg-[#002A4E] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#FFD700]">
                  KUDHI
                </span>
                <p className="mt-1 sm:mt-2 text-sm sm:text-lg lg:text-xl font-extrabold leading-tight text-[#002A4E]">
                  Vendez plus, publiez votre annonce
                </p>
                <p className="hidden sm:block mt-1 text-xs text-[#002A4E]/75">À partir de 100 FCFA · paiement Wave</p>
                <div className="mt-2 sm:mt-3 flex flex-wrap gap-2">
                  <button
                    onClick={() => setShowNewPostModal(true)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#002A4E] px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-bold text-white transition-colors hover:bg-[#003866]"
                  >
                    <Svg d={PATHS.plus} className="w-4 h-4" stroke={2.5} />
                    Publier
                  </button>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="hidden sm:inline-flex items-center rounded-md bg-white/70 px-3 py-2 text-sm font-semibold text-[#002A4E] transition-colors hover:bg-white"
                  >
                    Tarifs
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ CONTENU ============ */}
      <div className="w-[94%] md:w-[80%] mx-auto py-8 flex flex-col lg:flex-row gap-6">

        {/* Colonne catégories (desktop) */}
        <aside className="hidden lg:block w-64 flex-shrink-0 sticky top-28 self-start">
          <div className="overflow-hidden rounded-md bg-white ring-1 ring-slate-200/70">
            <div className="border-b border-slate-100 px-4 py-3.5">
              <p className="text-xs font-bold uppercase tracking-widest text-[#B8860B]">Catégories</p>
            </div>
            <nav className="max-h-[calc(100vh-11rem)] overflow-y-auto scrollbar-hide p-2">
              {loadingCategories ? (
                [1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={i} className="mb-1.5 h-10 rounded-md skeleton-shimmer" />)
              ) : categoriesFromDB.map((cat) => {
                const active = activeCategory === cat.categorie_id
                return (
                  <button
                    key={cat.categorie_id || 'all'}
                    onClick={() => setActiveCategory(cat.categorie_id)}
                    className={`group relative mb-0.5 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-sm font-semibold transition-all ${
                      active ? 'bg-[#002A4E] text-white shadow-sm' : 'text-slate-600 hover:bg-[#FFF8D6] hover:text-[#002A4E]'
                    }`}
                  >
                    <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md transition-colors ${
                      active ? 'bg-white/10 text-[#FFD700]' : 'text-[#F5B700] group-hover:text-[#E0A800]'
                    }`}>
                      <span className="material-icons text-[18px]">{cat.icon || 'category'}</span>
                    </span>
                    <span className="flex-1 truncate">{cat.libele_categorie}</span>
                    {active && <span className="h-1.5 w-1.5 rounded-full bg-[#FFD700]" />}
                  </button>
                )
              })}
            </nav>
          </div>
        </aside>

        {/* Colonne produits */}
        <main className="flex-1 min-w-0">

          {/* Recherche */}
          <div className="relative mb-4">
            <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
              <Svg d={PATHS.search} className="w-5 h-5" />
            </span>
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un produit, un vendeur…"
              className="w-full rounded-md border border-slate-200 bg-white py-3 pl-12 pr-4 text-sm text-[#002A4E] placeholder:text-slate-400 focus:border-amber-300 focus:outline-none focus:ring-4 focus:ring-amber-100"
            />
          </div>

          {/* Catégories (mobile) */}
          <div className="lg:hidden mb-6 flex gap-2 overflow-x-auto scrollbar-hide">
            {categoriesFromDB.map((cat) => {
              const active = activeCategory === cat.categorie_id
              return (
                <button
                  key={cat.categorie_id || 'all'}
                  onClick={() => setActiveCategory(cat.categorie_id)}
                  className={`flex flex-shrink-0 items-center gap-2 whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-semibold transition-all ${
                    active ? 'bg-[#002A4E] text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
                  }`}
                >
                  <span className={`flex h-6 w-6 items-center justify-center rounded ${active ? 'text-[#FFD700]' : 'text-[#F5B700]'}`}>
                    <span className="material-icons text-[16px]">{cat.icon || 'category'}</span>
                  </span>
                  {cat.libele_categorie}
                </button>
              )
            })}
          </div>

          {/* Deals du moment */}
          {(() => {
            const discountedProducts = products.filter(p => p.oldPrice)
            if (discountedProducts.length === 0) return null
            const N = discountedProducts.length
            // Clones uniquement si assez de produits pour défiler (sinon doublons visibles)
            const renderItems = N > 3 ? [...discountedProducts, ...discountedProducts.slice(0, 3)] : discountedProducts
            const activeDot = discountCarouselIndex % N
            const goNext = () => {
              setDiscountCarouselIndex(i => {
                const next = i + 1
                if (next >= N) {
                  setTimeout(() => {
                    setIsFlashResetting(true)
                    setDiscountCarouselIndex(0)
                    setTimeout(() => setIsFlashResetting(false), 50)
                  }, 520)
                }
                return next
              })
            }
            const goPrev = () => setDiscountCarouselIndex(i => (i - 1 + N) % N)
            return (
              <div className="mb-10 rounded-md bg-gradient-to-br from-[#FFF1F1] via-white to-[#FFF8E1] p-4 sm:p-6 ring-1 ring-red-100">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-red-500 text-white shadow-md shadow-red-300/50">
                      <Svg d={PATHS.bolt} className="w-5 h-5" />
                    </span>
                    <div>
                      <h2 className="text-lg sm:text-xl font-extrabold tracking-tight text-[#002A4E]">Deals du moment</h2>
                      <p className="text-xs sm:text-sm text-slate-500">Les meilleures réductions, à saisir vite</p>
                    </div>
                  </div>
                  {N > 3 && (
                    <div className="flex items-center gap-2">
                      <RoundButton onClick={goPrev} label="Précédent" d={PATHS.chevLeft} />
                      <span className="text-xs font-semibold text-slate-500 tabular-nums">{activeDot + 1}/{N}</span>
                      <RoundButton onClick={goNext} label="Suivant" d={PATHS.chevRight} />
                    </div>
                  )}
                </div>
                <div className="overflow-hidden -mx-1.5">
                  <div
                    className="flex"
                    style={{
                      transform: `translateX(-${discountCarouselIndex * (100 / 3)}%)`,
                      transition: isFlashResetting ? 'none' : 'transform 500ms ease-in-out',
                    }}
                  >
                    {renderItems.map((product, renderIdx) => {
                      const priceNum = parseFloat(String(product.price).replace(/[^0-9.]/g, '')) || 0
                      const oldPriceNum = parseFloat(String(product.oldPrice).replace(/[^0-9.]/g, '')) || 0
                      const discount = oldPriceNum > priceNum ? Math.round((1 - priceNum / oldPriceNum) * 100) : 0
                      return (
                        <div key={`${product.id}-${renderIdx}`} className="w-1/3 flex-shrink-0 px-1.5">
                          <button
                            onClick={() => navigate(`/product/${encodeId(product.id)}`)}
                            className="group block w-full overflow-hidden rounded-md bg-white text-left shadow-sm ring-1 ring-slate-100 transition-all hover:-translate-y-1 hover:shadow-lg"
                          >
                            <div className="relative aspect-[4/3] overflow-hidden">
                              <img src={product.image || headerImage} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                              {discount > 0 && <DiscountBadge value={discount} />}
                            </div>
                            <div className="p-2.5 sm:p-3">
                              <h4 className="text-xs sm:text-sm font-semibold text-[#002A4E] line-clamp-1">{product.name}</h4>
                              <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
                                <span className="text-sm sm:text-base font-extrabold text-red-600">{formatPrice(product.price)}</span>
                                <span className="text-[11px] sm:text-xs text-slate-400 line-through">{formatPrice(product.oldPrice)}</span>
                              </div>
                            </div>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
                {N > 3 && (
                  <div className="mt-4 flex justify-center gap-1.5">
                    {Array.from({ length: N }).map((_, idx) => (
                      <button
                        key={idx}
                        aria-label={`Deal ${idx + 1}`}
                        onClick={() => setDiscountCarouselIndex(idx)}
                        className={`h-1.5 rounded-full transition-all ${idx === activeDot ? 'w-5 bg-red-500' : 'w-1.5 bg-red-200'}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })()}

          {/* En-tête de la liste */}
          <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-sm font-bold uppercase tracking-widest text-[#B8860B]">Meilleures offres</p>
              <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#002A4E]">{activeCategoryLabel}</h2>
            </div>
            {!loadingProducts && (
              <p className="text-sm text-slate-500">
                {visibleProducts.length} annonce{visibleProducts.length > 1 ? 's' : ''}{query ? ` pour « ${searchQuery.trim()} »` : ''}
              </p>
            )}
          </div>

          {/* Grille produits */}
          <div className="grid grid-cols-2 2xl:grid-cols-3 gap-3 sm:gap-4">
            {loadingProducts ? (
              [1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="overflow-hidden rounded-md bg-white ring-1 ring-slate-100">
                  <div className="aspect-[4/3] skeleton-shimmer" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 w-3/4 rounded-full skeleton-shimmer" />
                    <div className="h-5 w-1/2 rounded-full skeleton-shimmer" />
                    <div className="h-10 rounded-md skeleton-shimmer" />
                  </div>
                </div>
              ))
            ) : visibleProducts.length === 0 ? (
              <div className="col-span-full rounded-md border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-md" style={GOLD_PILL}>
                  <Svg d={PATHS.search} className="w-6 h-6" />
                </span>
                <p className="mt-4 font-bold text-[#002A4E]">Aucun produit trouvé</p>
                <p className="mt-1 text-sm text-slate-500">
                  {query ? 'Essayez un autre mot-clé.' : 'Aucune annonce dans cette catégorie pour le moment.'}
                </p>
              </div>
            ) : (
              visibleProducts.map((product) => {
                const priceNum = typeof product.price === 'string' ? parseFloat(product.price.replace(/[^0-9]/g, '')) : product.price
                const oldPriceNum = product.oldPrice ? (typeof product.oldPrice === 'string' ? parseFloat(product.oldPrice.replace(/[^0-9]/g, '')) : product.oldPrice) : 0
                const discount = oldPriceNum > 0 ? Math.round((1 - priceNum / oldPriceNum) * 100) : 0
                const sellerInitials = (product.client_name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase()

                return (
                  <article
                    key={product.id}
                    className="group flex flex-col overflow-hidden rounded-md bg-white ring-1 ring-slate-200/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(0,42,78,0.35)] hover:ring-transparent"
                  >
                    <div
                      onClick={() => navigate(`/product/${encodeId(product.id)}`)}
                      className="relative aspect-[4/3] cursor-pointer overflow-hidden bg-slate-100"
                    >
                      <img
                        src={product.image || headerImage}
                        alt={product.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {discount > 0 && <DiscountBadge value={discount} />}
                      {(product.etat_appareil_label || product.condition_label) && (
                        <span className={`absolute right-2.5 top-2.5 rounded-md px-2 py-1 text-[11px] sm:text-xs font-bold shadow-md ${conditionClass(product.etat_appareil)}`}>
                          {product.etat_appareil_label || product.condition_label}
                        </span>
                      )}
                      <span className="absolute bottom-2.5 left-2.5 inline-flex items-center gap-1 rounded-md bg-black/45 px-2 py-1 text-[11px] sm:text-xs font-semibold text-white backdrop-blur-sm">
                        <Svg d={PATHS.eye} className="w-3.5 h-3.5" />
                        {product.views || 0}
                      </span>
                    </div>

                    <div className="flex flex-1 flex-col p-3 sm:p-4">
                      <h3
                        onClick={() => navigate(`/product/${encodeId(product.id)}`)}
                        className="cursor-pointer text-sm sm:text-[15px] font-bold leading-snug text-[#002A4E] line-clamp-2 min-h-[2.6em] transition-colors hover:text-[#B8860B]"
                      >
                        {product.name}
                      </h3>

                      <div className="mt-2 flex flex-wrap items-baseline gap-x-2">
                        <span className={`text-base sm:text-lg font-extrabold ${discount > 0 ? 'text-red-600' : 'text-[#002A4E]'}`}>
                          {formatPrice(product.price) || 'Prix sur demande'}
                        </span>
                        {product.oldPrice && <span className="text-xs text-slate-400 line-through">{formatPrice(product.oldPrice)}</span>}
                      </div>

                      {/* Vendeur + actions */}
                      <div className="mt-3 flex items-center gap-2 rounded-md bg-slate-50 p-2">
                        {product.client_photo ? (
                          <img src={product.client_photo} alt="" className="h-8 w-8 flex-shrink-0 rounded-full object-cover" />
                        ) : (
                          <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold" style={GOLD_PILL}>{sellerInitials}</span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs sm:text-sm font-semibold text-[#002A4E]">{product.client_name || 'Vendeur'}</p>
                          <p className="truncate text-[11px] text-slate-400">{product.date}</p>
                        </div>
                        <div className="flex flex-shrink-0 gap-1">
                          <ActionButton title="Appeler" d={PATHS.phone} onClick={() => { if (product.client_phone) window.location.href = `tel:${product.client_phone}` }} />
                          <ActionButton title="Message" d={PATHS.message} onClick={() => openConversation(product)} />
                          <ActionButton title="WhatsApp" whatsapp onClick={() => openWhatsApp(product.client_phone)} />
                        </div>
                      </div>

                      <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
                        <span className="inline-flex items-center gap-1"><Svg d={PATHS.heart} className="w-4 h-4" />{product.likes}</span>
                        <span className="inline-flex items-center gap-1"><Svg d={PATHS.chat} className="w-4 h-4" />{product.comments}</span>
                      </div>
                    </div>
                  </article>
                )
              })
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-10 flex items-center justify-center gap-2" aria-label="Pagination">
              <RoundButton onClick={() => handlePageChange(Math.max(1, page - 1))} disabled={page === 1} label="Page précédente" d={PATHS.chevLeft} large />
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`h-11 min-w-[2.75rem] rounded-md px-3 text-sm font-bold transition-all ${
                    page === pageNum
                      ? 'bg-[#FFD700] text-[#002A4E] shadow-md shadow-amber-300/50'
                      : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-amber-300 hover:text-[#002A4E]'
                  }`}
                >
                  {pageNum}
                </button>
              ))}
              <RoundButton onClick={() => handlePageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages} label="Page suivante" d={PATHS.chevRight} large />
            </nav>
          )}
        </main>

        {/* Colonne sponsorisée */}
        <aside className="hidden xl:block w-72 flex-shrink-0 sticky top-28 self-start space-y-5">

          {/* Bannière sponsorisée */}
          <SponsoredCard label="Bannière sponsorisée">
            {loadingAdBanners ? (
              <div className="h-48 skeleton-shimmer" />
            ) : adBanners.length > 0 ? (
              <div className="group relative h-48">
                <img
                  key={adBannerIndex}
                  src={adBanners[adBannerIndex]?.image_url || headerImage}
                  alt={adBanners[adBannerIndex]?.titre || 'Publicité'}
                  className="w-full h-full object-cover animate-fade-in"
                  onError={(e) => { e.target.src = headerImage }}
                />
                <MediaStats views={adBanners[adBannerIndex]?.views} likes={adBanners[adBannerIndex]?.likes} comments={adBanners[adBannerIndex]?.comments} />
                <HoverAction
                  label="Voir les détails"
                  onClick={() => navigate(`/staff-banner/${encodeId(adBanners[adBannerIndex]?.id || adBanners[adBannerIndex]?.banniere_id)}`)}
                />
              </div>
            ) : (
              <EmptyAd label="Publicité" staff={isAdminOrStaff} height="h-48" />
            )}
          </SponsoredCard>

          {/* Vidéo sponsorisée */}
          <SponsoredCard label="Vidéo sponsorisée">
            {loadingAdVideos ? (
              <div className="h-48 skeleton-shimmer" />
            ) : adVideos.length > 0 ? (
              <div className="relative h-48 overflow-hidden">
                <div className="flex h-full transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${adVideoIndex * 100}%)` }}>
                  {adVideos.map((video, index) => (
                    <div key={video.id} className="group relative h-full w-full flex-shrink-0 cursor-pointer" onClick={() => navigate(`/video/${encodeId(video.id)}`)}>
                      <video src={video.video_url} className="w-full h-full object-cover" autoPlay={index === adVideoIndex} muted playsInline />
                      <MediaStats views={video.views} likes={video.likes} comments={video.comments} />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/30">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFD700] text-[#002A4E] opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
                          <Svg d={PATHS.play} className="w-5 h-5 ml-0.5" fill />
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyAd label="Vidéo sponsorisée" staff={isAdminOrStaff} height="h-48" />
            )}
          </SponsoredCard>

          {/* Article sponsorisé */}
          <SponsoredCard label="Article sponsorisé">
            {loadingAdPosts ? (
              <div className="h-80 skeleton-shimmer" />
            ) : adPosts.length > 0 ? (
              <div
                className="group relative h-80"
                onMouseEnter={() => setIsAdPostHovered(true)}
                onMouseLeave={() => setIsAdPostHovered(false)}
              >
                <img
                  key={adPostIndex}
                  src={adPosts[adPostIndex]?.images?.[0] || headerImage}
                  alt={adPosts[adPostIndex]?.libele || 'Publication'}
                  className="w-full h-full object-cover animate-fade-in"
                  onError={(e) => { e.target.src = headerImage }}
                />
                {(() => {
                  const currentPost = adPosts[adPostIndex]
                  if (currentPost?.oldPrice && currentPost?.price) {
                    const discount = Math.round((1 - currentPost.price / currentPost.oldPrice) * 100)
                    if (discount > 0) return <DiscountBadge value={discount} />
                  }
                  return null
                })()}
                {adPosts[adPostIndex]?.etat_label && (
                  <span className={`absolute right-2.5 top-2.5 z-10 rounded-md px-2 py-1 text-xs font-bold shadow-md ${conditionClass(adPosts[adPostIndex].etat_appareil)}`}>
                    {adPosts[adPostIndex].etat_label}
                  </span>
                )}
                <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-[#001A33]/90 via-[#001A33]/10 to-transparent p-4">
                  <h3 className="text-sm font-bold text-white line-clamp-2">{adPosts[adPostIndex]?.libele || adPosts[adPostIndex]?.name}</h3>
                  {adPosts[adPostIndex]?.price > 0 && (
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="text-lg font-extrabold text-[#FFD700]">{formatPrice(adPosts[adPostIndex].price)}</span>
                      {adPosts[adPostIndex]?.oldPrice && (
                        <span className="text-xs text-white/60 line-through">{formatPrice(adPosts[adPostIndex].oldPrice)}</span>
                      )}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-3 text-xs text-white/80">
                    <span className="inline-flex items-center gap-1"><Svg d={PATHS.eye} className="w-3.5 h-3.5" />{adPosts[adPostIndex]?.vues || 0}</span>
                    <span className="inline-flex items-center gap-1"><Svg d={PATHS.heart} className="w-3.5 h-3.5" />{adPosts[adPostIndex]?.likes_count || 0}</span>
                    <span className="inline-flex items-center gap-1"><Svg d={PATHS.chat} className="w-3.5 h-3.5" />{adPosts[adPostIndex]?.comments_count || 0}</span>
                  </div>
                </div>
                <HoverAction
                  label="Voir détails"
                  onClick={() => { if (adPosts[adPostIndex]?.id) navigate(`/product/${encodeId(adPosts[adPostIndex].id)}`) }}
                />
                {adPosts.length > 1 && (
                  <div className="absolute left-1/2 top-3 z-10 flex -translate-x-1/2 gap-1">
                    {adPosts.slice(0, 8).map((_, i) => (
                      <span key={i} className={`h-1 rounded-full transition-all ${i === adPostIndex % 8 ? 'w-4 bg-[#FFD700]' : 'w-1 bg-white/60'}`} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <EmptyAd label="Publication sponsorisée" staff={isAdminOrStaff} height="h-80" />
            )}
          </SponsoredCard>

          {/* Contacts récents */}
          <div className="overflow-hidden rounded-md bg-white ring-1 ring-slate-200/70">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h3 className="font-bold text-[#002A4E]">Contacts récents</h3>
              {recentContacts.length > 0 && (
                <button onClick={() => navigate('/messages')} className="text-xs font-semibold text-[#B8860B] hover:underline underline-offset-2">Tout voir</button>
              )}
            </div>
            <div className="p-3">
              {loadingContacts ? (
                <div className="space-y-2 p-1">
                  {[1, 2, 3].map(i => <div key={i} className="h-12 rounded-md skeleton-shimmer" />)}
                </div>
              ) : recentContacts.length > 0 ? (
                <div className="max-h-64 space-y-1 overflow-y-auto">
                  {recentContacts.map((contact) => {
                    const initials = ((contact.first_name || '').charAt(0) + (contact.name || '').charAt(0)).toUpperCase() || '?'
                    return (
                      <button
                        key={contact.id}
                        onClick={() => navigate('/messages', {
                          state: { openConversationWith: { id: contact.id, name: contact.full_name || contact.name, phone: contact.phone, avatar: contact.avatar } },
                        })}
                        className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-slate-50"
                      >
                        <div className="relative flex-shrink-0">
                          {contact.avatar ? (
                            <img
                              src={contact.avatar.startsWith('http') ? contact.avatar : mediaUrl(contact.avatar)}
                              alt=""
                              className="h-10 w-10 rounded-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
                            />
                          ) : null}
                          <span
                            className="h-10 w-10 items-center justify-center rounded-full text-sm font-bold"
                            style={{ ...GOLD_PILL, display: contact.avatar ? 'none' : 'flex' }}
                          >
                            {initials}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-[#002A4E]">{contact.full_name || contact.name || 'Utilisateur'}</p>
                          <p className="truncate text-xs text-slate-400">{contact.phone || 'Contact'}</p>
                        </div>
                        <Svg d={PATHS.chevRight} className="w-4 h-4 flex-shrink-0 text-slate-300" />
                      </button>
                    )
                  })}
                </div>
              ) : (
                <div className="px-4 py-8 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md" style={GOLD_PILL}>
                    <Svg d={PATHS.message} className="w-5 h-5" />
                  </span>
                  <p className="mt-3 text-sm font-semibold text-[#002A4E]">Aucun contact récent</p>
                  <p className="mt-1 text-xs text-slate-500">Écrivez à un vendeur pour démarrer une conversation.</p>
                </div>
              )}
            </div>
          </div>
        </aside>
      </div>

      {/* Bouton création de publication */}
      <button
        onClick={() => setShowNewPostModal(true)}
        aria-label="Publier une annonce"
        title="Publier une annonce"
        className="group fixed bottom-4 right-4 sm:bottom-6 sm:right-6 md:bottom-8 md:right-8 z-40 flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#FFE45C] via-[#FFD700] to-[#F5B700] text-[#002A4E] shadow-xl shadow-amber-400/40 transition-all duration-300 hover:scale-110 hover:shadow-2xl"
      >
        <span className="absolute inset-0 rounded-full border-2 border-[#FFD700] animate-ping opacity-60" />
        <Svg d={PATHS.plus} className="relative w-7 h-7 transition-transform duration-300 group-hover:rotate-90" stroke={2.8} />
      </button>

      {/* Modal Nouvelle publication */}
      {showNewPostModal && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowNewPostModal(false)} />
          <div className="relative w-full sm:max-w-3xl rounded-t-md sm:rounded-md bg-white p-6 sm:p-10 shadow-2xl animate-scale-in">
            <button
              onClick={() => setShowNewPostModal(false)}
              aria-label="Fermer"
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100"
            >
              <Svg d={PATHS.close} className="w-5 h-5" />
            </button>
            <p className="text-sm font-bold uppercase tracking-widest text-[#B8860B]">Nouvelle publication</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#002A4E]">Que voulez-vous publier ?</h2>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { to: '/create-post', title: 'Images', text: 'De 3 à 9 photos', d: PATHS.image },
                { to: '/create-video', title: 'Vidéo', text: 'Montrez en action', d: PATHS.video },
                { to: '/create-banner', title: 'Bannière', text: 'Visibilité maximale', d: PATHS.banner },
              ].map((opt) => (
                <button
                  key={opt.to}
                  onClick={() => { setShowNewPostModal(false); navigate(opt.to) }}
                  className="group flex sm:flex-col items-center gap-4 rounded-md p-5 sm:p-7 text-left sm:text-center ring-1 ring-slate-200 transition-all hover:-translate-y-1 hover:bg-[#FFD700] hover:ring-transparent hover:shadow-xl hover:shadow-amber-300/40"
                >
                  <span className="flex h-14 w-14 sm:h-16 sm:w-16 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[#FFE45C] via-[#FFD700] to-[#F5B700] text-[#002A4E] transition-colors group-hover:bg-none group-hover:bg-[#002A4E] group-hover:text-[#FFD700]">
                    <Svg d={opt.d} className="w-7 h-7" />
                  </span>
                  <span>
                    <span className="block text-lg font-bold text-[#002A4E]">{opt.title}</span>
                    <span className="block text-sm text-slate-500 group-hover:text-[#002A4E]/70">{opt.text}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Éléments d'interface de la page Marketplace
// ---------------------------------------------------------------------------

const GOLD_PILL = { background: 'linear-gradient(135deg, #FFE45C 0%, #FFD700 50%, #F5B700 100%)', color: '#002A4E' }

const PATHS = {
  eye: 'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  chat: 'M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z',
  message: 'M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z',
  phone: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z',
  search: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z',
  bolt: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z',
  chevLeft: 'M15.75 19.5L8.25 12l7.5-7.5',
  chevRight: 'M8.25 4.5l7.5 7.5-7.5 7.5',
  play: 'M8 5.14v13.72a1 1 0 001.52.85l11.03-6.86a1 1 0 000-1.7L9.52 4.29A1 1 0 008 5.14z',
  plus: 'M12 4.5v15m7.5-7.5h-15',
  close: 'M6 18L18 6M6 6l12 12',
  image: 'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A1.5 1.5 0 0021.75 19.5V4.5A1.5 1.5 0 0020.25 3H3.75A1.5 1.5 0 002.25 4.5v15A1.5 1.5 0 003.75 21zM15 8.25h.008v.008H15V8.25z',
  video: 'M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9A2.25 2.25 0 0013.5 5.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z',
  banner: 'M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38a1.125 1.125 0 01-1.536-.48 20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46',
}

const WHATSAPP_PATH = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z'

// Couleur du badge selon l'état de l'appareil
const conditionClass = (etat) => ({
  neuf: 'bg-blue-600 text-white',
  scelle: 'bg-green-600 text-white',
  quasi_neuf: 'bg-teal-500 text-white',
  excellent: 'bg-indigo-500 text-white',
  bon: 'bg-yellow-400 text-[#002A4E]',
  moyen: 'bg-orange-500 text-white',
  usage: 'bg-red-500 text-white',
  reconditionne: 'bg-purple-500 text-white',
  'reconditionné': 'bg-purple-500 text-white',
  occasion: 'bg-amber-500 text-white',
}[etat] || 'bg-slate-600 text-white')

const Svg = ({ d, className = 'w-5 h-5', fill = false, stroke = 1.8 }) => (
  <svg className={className} viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'} stroke={fill ? 'none' : 'currentColor'} strokeWidth={stroke} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
)



const RoundButton = ({ onClick, label, d, disabled = false, large = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    className={`flex items-center justify-center rounded-md bg-white text-[#002A4E] ring-1 ring-slate-200 transition-all hover:ring-amber-300 disabled:cursor-not-allowed disabled:opacity-40 ${large ? 'h-11 w-11' : 'h-8 w-8'}`}
  >
    <Svg d={d} className={large ? 'w-5 h-5' : 'w-4 h-4'} stroke={2.2} />
  </button>
)

const DiscountBadge = ({ value }) => (
  <span className="absolute left-2.5 top-2.5 z-10 rounded-md bg-red-600 px-2 py-1 text-[11px] sm:text-xs font-extrabold text-white shadow-md">-{value}%</span>
)

const ActionButton = ({ title, d, onClick, whatsapp = false }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onClick() }}
    title={title}
    aria-label={title}
    className={`flex h-8 w-8 items-center justify-center rounded-md transition-all hover:-translate-y-0.5 ${
      whatsapp ? 'bg-[#25D366] text-white hover:bg-[#1ebe5b]' : 'bg-[#FFD700] text-[#002A4E] hover:bg-[#F5C800]'
    }`}
  >
    {whatsapp ? (
      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={WHATSAPP_PATH} /></svg>
    ) : (
      <Svg d={d} className="w-4 h-4" stroke={2} />
    )}
  </button>
)

const SponsoredCard = ({ label, children }) => (
  <div className="overflow-hidden rounded-md bg-white ring-1 ring-slate-200/70">
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</span>
      <span className="rounded-full bg-[#FFF8D6] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#B8860B]">Sponsorisé</span>
    </div>
    {children}
  </div>
)

const MediaStats = ({ views, likes, comments }) => (
  <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2.5 pt-8 text-xs text-white">
    <span className="inline-flex items-center gap-1"><Svg d={PATHS.eye} className="w-3.5 h-3.5" />{views ?? 0}</span>
    <span className="inline-flex items-center gap-1"><Svg d={PATHS.heart} className="w-3.5 h-3.5" />{likes ?? 0}</span>
    <span className="inline-flex items-center gap-1"><Svg d={PATHS.chat} className="w-3.5 h-3.5" />{comments ?? 0}</span>
  </div>
)

const HoverAction = ({ label, onClick }) => (
  <div className="absolute inset-0 z-20 flex items-center justify-center bg-[#001A33]/50 opacity-0 backdrop-blur-[2px] transition-opacity duration-300 group-hover:opacity-100">
    <button
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-md bg-[#FFD700] px-5 py-3 text-sm font-bold text-[#002A4E] shadow-lg transition-transform hover:scale-105"
    >
      <Svg d={PATHS.eye} className="w-4 h-4" stroke={2} />
      {label}
    </button>
  </div>
)

const EmptyAd = ({ label, staff, height }) => (
  <div className={`mx-4 mb-4 flex ${height} flex-col items-center justify-center rounded-md border-2 border-dashed border-slate-200 text-center`}>
    <span className="flex h-11 w-11 items-center justify-center rounded-md" style={GOLD_PILL}>
      <Svg d={PATHS.banner} className="w-5 h-5" />
    </span>
    <p className="mt-3 text-sm font-semibold text-[#002A4E]">{label}</p>
    <p className="mt-0.5 text-xs text-slate-400">{staff ? 'Espace réservé au staff' : 'Votre publicité ici'}</p>
  </div>
)

export default DashboardPage